import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../App.css';
import './LandingPage.css';
import './SemaforOnline.css';
import { useSeo } from '../hooks/useSeo';
import { useJsonLd } from '../hooks/useJsonLd';
import FaqItem from './FaqItem';
import PublicPageNav from './PublicPageNav';


const TEAM_COLORS = [
  '#00f5a0',
  '#38bdf8',
  '#facc15',
  '#fb7185',
  '#a78bfa',
  '#f97316',
];

const FAQ_ITEMS = [
  {
    question: 'Je li online semafor besplatan?',
    answer:
      'Da. Semafor je besplatan i radi direktno u pregledniku. Ne trebaš instalirati aplikaciju, otvarati račun ni spremati podatke.',
  },
  {
    question: 'Mogu li koristiti semafor na TV-u?',
    answer:
      'Možeš. Otvori stranicu na laptopu, tabletu ili mobitelu, spoji uređaj na TV i uključi full-screen prikaz. Semafor je napravljen tako da rezultat i vrijeme budu veliki i čitljivi.',
  },
  {
    question: 'Radi li brojač vremena za poluvremena?',
    answer:
      'Da. Možeš postaviti trajanje poluvremena, prebaciti broj poluvremena i resetirati timer između dijelova utakmice. Timer može odbrojavati prema nuli ili brojati prema gore.',
  },
  {
    question: 'Ima li semafor zvuk na kraju vremena?',
    answer:
      'Da. Ako je zvuk uključen, semafor pusti kratki signal kada timer dođe do kraja. Preglednik može tražiti korisničku interakciju prije reprodukcije zvuka, što je standardna sigurnosna postavka.',
  },
  {
    question: 'Sprema li StatArena rezultat iz ovog semafora?',
    answer:
      'Ne. Ovaj online semafor je brz javni alat i ne sprema rezultat. Ako želiš trajno pratiti rezultate, statistiku igrača i rang listu, koristi glavni StatArena timski sustav.',
  },
];

const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

const SOFTWARE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Online semafor za nogomet',
  url: 'https://statarena.app/semafor-online',
  description:
    'Besplatni digitalni semafor s brojačem vremena, rezultatom i kartonima. Radi u pregledniku na mobitelu, tabletu, laptopu i TV-u.',
  applicationCategory: 'SportsApplication',
  operatingSystem: 'Web',
  browserRequirements: 'Requires JavaScript. Requires HTML5.',
  inLanguage: 'hr',
  isAccessibleForFree: true,
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'EUR',
  },
  featureList: [
    'Brojač vremena s odbrojavanjem i brojanjem prema gore',
    'Rezultat za dvije ekipe',
    'Žuti i crveni kartoni',
    'Praćenje poluvremena',
    'Full-screen prikaz za TV',
    'Zvuk na kraju vremena',
    'Prilagodljive boje ekipa',
  ],
  publisher: {
    '@type': 'Organization',
    name: 'StatArena',
    url: 'https://statarena.app',
    logo: 'https://statarena.app/icon-512.png',
  },
};

const BREADCRUMB_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Početna',
      item: 'https://statarena.app/',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Online semafor',
      item: 'https://statarena.app/semafor-online',
    },
  ],
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const formatTime = (seconds) => {
  const safeSeconds = Math.max(0, seconds);
  const mins = Math.floor(safeSeconds / 60);
  const secs = safeSeconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const playHorn = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.32, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.9);
    gain.connect(ctx.destination);

    [220, 185].forEach((freq, index) => {
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + index * 0.18);
      osc.connect(gain);
      osc.start(ctx.currentTime + index * 0.18);
      osc.stop(ctx.currentTime + 0.9);
    });

    setTimeout(() => ctx.close(), 1200);
  } catch (error) {
    console.warn('Zvuk semafora nije dostupan:', error);
  }
};

const SemaforOnline = () => {
  useSeo({
    title: 'Online semafor za nogomet - brojač vremena i rezultat | StatArena',
    description:
      'Besplatni online semafor za nogomet. Pokreni timer, prati rezultat, kartone i poluvrijeme na mobitelu, tabletu ili TV-u pored terena.',
    canonical: 'https://statarena.app/semafor-online',
    robots: 'index, follow, max-image-preview:large',
    ogUrl: 'https://statarena.app/semafor-online',
    ogTitle: 'Online semafor za nogomet',
    ogDescription:
      'Digitalni semafor s timerom, rezultatom i kartonima. Radi full-screen na TV-u, tabletu i mobitelu.',
  });

  useJsonLd('semafor-software', SOFTWARE_JSONLD);
  useJsonLd('semafor-faq', FAQ_JSONLD);
  useJsonLd('semafor-breadcrumb', BREADCRUMB_JSONLD);

  const boardRef = useRef(null);
  const [teamAName, setTeamAName] = useState('Domaci');
  const [teamBName, setTeamBName] = useState('Gosti');
  const [teamAColor, setTeamAColor] = useState(TEAM_COLORS[0]);
  const [teamBColor, setTeamBColor] = useState(TEAM_COLORS[1]);
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [yellowA, setYellowA] = useState(0);
  const [yellowB, setYellowB] = useState(0);
  const [redA, setRedA] = useState(0);
  const [redB, setRedB] = useState(0);
  const [period, setPeriod] = useState(1);
  const [minutes, setMinutes] = useState(20);
  const [secondsLeft, setSecondsLeft] = useState(20 * 60);
  const [running, setRunning] = useState(false);
  const [countDirection, setCountDirection] = useState('down');
  const [soundEnabled, setSoundEnabled] = useState(true);

  const maxSeconds = useMemo(() => clamp(Number(minutes) || 1, 1, 120) * 60, [minutes]);
  const isFullscreen = Boolean(document.fullscreenElement);

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => {
      setSecondsLeft((current) => {
        if (countDirection === 'up') {
          const next = current + 1;
          if (next >= maxSeconds) {
            window.clearInterval(timer);
            setRunning(false);
            if (soundEnabled) playHorn();
            return maxSeconds;
          }
          return next;
        }

        const next = current - 1;
        if (next <= 0) {
          window.clearInterval(timer);
          setRunning(false);
          if (soundEnabled) playHorn();
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [countDirection, maxSeconds, running, soundEnabled]);

  useEffect(() => {
    setSecondsLeft(countDirection === 'up' ? 0 : maxSeconds);
    setRunning(false);
  }, [countDirection, maxSeconds]);

  const adjustScore = (team, delta) => {
    if (team === 'a') setScoreA((value) => Math.max(0, value + delta));
    else setScoreB((value) => Math.max(0, value + delta));
  };

  const resetMatch = () => {
    setRunning(false);
    setScoreA(0);
    setScoreB(0);
    setYellowA(0);
    setYellowB(0);
    setRedA(0);
    setRedB(0);
    setPeriod(1);
    setSecondsLeft(countDirection === 'up' ? 0 : maxSeconds);
  };

  const resetTimer = () => {
    setRunning(false);
    setSecondsLeft(countDirection === 'up' ? 0 : maxSeconds);
  };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (boardRef.current?.requestFullscreen) {
        await boardRef.current.requestFullscreen();
      }
    } catch (error) {
      console.warn('Fullscreen nije dostupan:', error);
    }
  };

  const timerProgress = maxSeconds > 0
    ? (countDirection === 'up'
      ? (secondsLeft / maxSeconds) * 100
      : ((maxSeconds - secondsLeft) / maxSeconds) * 100)
    : 0;

  return (
    <div className="landing scoreboard-page">
      <PublicPageNav className="scoreboard-nav" />

      <main className="scoreboard-shell">
        <section
          className="scoreboard-stage"
          ref={boardRef}
          style={{
            '--team-a': teamAColor,
            '--team-b': teamBColor,
            '--timer-progress': `${timerProgress}%`,
          }}
        >
          <div className="scoreboard-topbar">
            <div>
              <span className="scoreboard-eyebrow">Online semafor</span>
              <h1>Digitalni semafor za nogomet</h1>
            </div>
            <button type="button" className="scoreboard-icon-btn" onClick={toggleFullscreen}>
              {isFullscreen ? 'Izađi iz full screena' : 'Full screen'}
            </button>
          </div>

          <div className="scoreboard-display">
            <div className="scoreboard-team scoreboard-team-a">
              <input
                value={teamAName}
                onChange={(e) => setTeamAName(e.target.value.slice(0, 24))}
                aria-label="Naziv domaće ekipe"
              />
              <div className="scoreboard-score">{scoreA}</div>
              <div className="scoreboard-score-controls">
                <button type="button" onClick={() => adjustScore('a', -1)}>-</button>
                <button type="button" onClick={() => adjustScore('a', 1)}>+</button>
              </div>
            </div>

            <div className="scoreboard-clock-panel">
              <div className="scoreboard-period">Poluvrijeme {period}</div>
              <div className={`scoreboard-clock ${secondsLeft <= 60 && countDirection === 'down' ? 'is-warning' : ''}`}>
                {formatTime(secondsLeft)}
              </div>
              <div className="scoreboard-progress">
                <span />
              </div>
              <div className="scoreboard-clock-actions">
                <button type="button" className="btn btn-primary" onClick={() => setRunning((value) => !value)}>
                  {running ? 'Pauza' : 'Start'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetTimer}>
                  Reset timer
                </button>
              </div>
            </div>

            <div className="scoreboard-team scoreboard-team-b">
              <input
                value={teamBName}
                onChange={(e) => setTeamBName(e.target.value.slice(0, 24))}
                aria-label="Naziv gostujuće ekipe"
              />
              <div className="scoreboard-score">{scoreB}</div>
              <div className="scoreboard-score-controls">
                <button type="button" onClick={() => adjustScore('b', -1)}>-</button>
                <button type="button" onClick={() => adjustScore('b', 1)}>+</button>
              </div>
            </div>
          </div>

          <div className="scoreboard-cards-row">
            <div className="scoreboard-card-controls">
              <span>{teamAName}</span>
              <button type="button" className="yellow-card" onClick={() => setYellowA((v) => v + 1)}>Žuti {yellowA}</button>
              <button type="button" className="red-card" onClick={() => setRedA((v) => v + 1)}>Crveni {redA}</button>
            </div>
            <div className="scoreboard-card-controls">
              <span>{teamBName}</span>
              <button type="button" className="yellow-card" onClick={() => setYellowB((v) => v + 1)}>Žuti {yellowB}</button>
              <button type="button" className="red-card" onClick={() => setRedB((v) => v + 1)}>Crveni {redB}</button>
            </div>
          </div>
        </section>

        <section className="scoreboard-controls">
          <div className="scoreboard-control-group">
            <h2>Postavke utakmice</h2>
            <div className="scoreboard-control-grid">
              <label>
                <span>Trajanje poluvremena</span>
                <input
                  type="number"
                  min="1"
                  max="120"
                  value={minutes}
                  onChange={(e) => setMinutes(clamp(Number(e.target.value) || 1, 1, 120))}
                />
              </label>
              <label>
                <span>Poluvrijeme</span>
                <input
                  type="number"
                  min="1"
                  max="9"
                  value={period}
                  onChange={(e) => setPeriod(clamp(Number(e.target.value) || 1, 1, 9))}
                />
              </label>
              <label>
                <span>Smjer brojenja</span>
                <select value={countDirection} onChange={(e) => setCountDirection(e.target.value)}>
                  <option value="down">Odbrojavanje</option>
                  <option value="up">Broji prema gore</option>
                </select>
              </label>
              <label className="scoreboard-toggle">
                <input
                  type="checkbox"
                  checked={soundEnabled}
                  onChange={(e) => setSoundEnabled(e.target.checked)}
                />
                <span>Zvuk na kraj vremena</span>
              </label>
            </div>
          </div>

          <div className="scoreboard-control-group">
            <h2>Boje ekipa</h2>
            <div className="scoreboard-swatches">
              <div>
                <span>{teamAName}</span>
                <div className="scoreboard-swatch-row">
                  {TEAM_COLORS.map((color) => (
                    <button
                      key={`a-${color}`}
                      type="button"
                      className={teamAColor === color ? 'active' : ''}
                      style={{ background: color }}
                      onClick={() => setTeamAColor(color)}
                      aria-label={`Boja domaće ekipe ${color}`}
                    />
                  ))}
                </div>
              </div>
              <div>
                <span>{teamBName}</span>
                <div className="scoreboard-swatch-row">
                  {TEAM_COLORS.map((color) => (
                    <button
                      key={`b-${color}`}
                      type="button"
                      className={teamBColor === color ? 'active' : ''}
                      style={{ background: color }}
                      onClick={() => setTeamBColor(color)}
                      aria-label={`Boja gostujuće ekipe ${color}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="scoreboard-control-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setPeriod((value) => clamp(value + 1, 1, 9))}>
              Sljedeće poluvrijeme
            </button>
            <button type="button" className="btn btn-danger" onClick={resetMatch}>
              Reset utakmice
            </button>
          </div>
        </section>

        <section className="landing-section scoreboard-seo-section">
          <div className="section-header landing-section-header">
            <h2>Online semafor za nogomet, futsal i rekreativne termine</h2>
            <p className="landing-section-sub">
              Ovaj digitalni semafor možeš otvoriti na mobitelu, tabletu, laptopu
              ili TV-u pored terena. Prati rezultat, vrijeme, poluvrijeme i kartone
              bez instalacije aplikacije i bez registracije.
            </p>
          </div>

          <div className="landing-features">
            <article className="landing-feature">
              <div className="landing-feature-icon"><span role="img" aria-label="TV">📺</span></div>
              <h3>Semafor na TV-u ili tabletu</h3>
              <p>
                Uključi full-screen prikaz i postavi uređaj pokraj terena. Rezultat
                i brojač vremena ostaju dovoljno veliki za ekipu, gledatelje i
                organizatora utakmice.
              </p>
            </article>

            <article className="landing-feature">
              <div className="landing-feature-icon"><span role="img" aria-label="Sat">⏱️</span></div>
              <h3>Brojač vremena za poluvrijeme</h3>
              <p>
                Postavi trajanje poluvremena, pokreni timer i zaustavi ga kada
                treba pauza. Možeš koristiti odbrojavanje ili brojanje prema gore,
                ovisno o načinu igre.
              </p>
            </article>

            <article className="landing-feature">
              <div className="landing-feature-icon"><span role="img" aria-label="Karton">🟨</span></div>
              <h3>Rezultat i kartoni na jednom mjestu</h3>
              <p>
                Dodaj golove jednim klikom, smanji rezultat ako se pogriješi i prati
                žute i crvene kartone za obje ekipe. Sve je vidljivo na jednom
                čistom ekranu.
              </p>
            </article>
          </div>
        </section>

        <section className="landing-section landing-section-alt scoreboard-seo-section">
          <div className="section-header landing-section-header">
            <h2>Za koje utakmice možeš koristiti digitalni semafor</h2>
            <p className="landing-section-sub">
              Alat je napravljen za rekreativni nogomet, ali radi za svaki sport
              gdje trebaš jednostavan rezultat i timer.
            </p>
          </div>

          <div className="landing-steps">
            <div className="landing-step">
              <div className="landing-step-num">1</div>
              <h3>Mali nogomet i futsal</h3>
              <p>Idealno za termine u dvorani, balonu ili na umjetnoj travi.</p>
            </div>
            <div className="landing-step">
              <div className="landing-step-num">2</div>
              <h3>Turniri i mini lige</h3>
              <p>Koristi isti semafor kroz više utakmica i brzo resetiraj rezultat.</p>
            </div>
            <div className="landing-step">
              <div className="landing-step-num">3</div>
              <h3>Školska natjecanja</h3>
              <p>Jednostavan prikaz za razredne turnire, sportske dane i treninge.</p>
            </div>
            <div className="landing-step">
              <div className="landing-step-num">4</div>
              <h3>Trening utakmice</h3>
              <p>Trener ili organizator može pratiti vrijeme, rezultat i kartone.</p>
            </div>
          </div>
        </section>

        <section className="landing-section scoreboard-seo-section">
          <div className="section-header landing-section-header">
            <h2>Često postavljena pitanja o online semaforu</h2>
            <p className="landing-section-sub">
              Kratki odgovori za sve koji traže semafor online, brojač vremena za
              nogomet ili digitalni rezultat za utakmicu.
            </p>
          </div>

          <div className="faq-list">
            {FAQ_ITEMS.map((item) => (
              <FaqItem key={item.question} question={item.question}>
                <p>{item.answer}</p>
              </FaqItem>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default SemaforOnline;

